import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView, trackInteraction, trackFormSubmission, hasAnalyticsConsent } from '../lib/analytics';

interface AnalyticsEvent {
  category: string;
  action: string;
  label?: string;
  value?: number;
}

export function useAnalytics() {
  const location = useLocation();

  // Track page views
  useEffect(() => {
    if (hasAnalyticsConsent()) {
      trackPageView();
    }
  }, [location.pathname]);

  // Track custom events
  const trackEvent = useCallback((event: AnalyticsEvent) => {
    if (hasAnalyticsConsent()) {
      trackInteraction(event.category, event.action, event.label);
    }
  }, []);

  // Track form submissions
  const trackForm = useCallback((formName: string, success: boolean) => {
    if (hasAnalyticsConsent()) {
      trackFormSubmission(formName, success);
    }
  }, []);

  // Track scroll depth
  const trackScrollDepth = useCallback(() => {
    if (!hasAnalyticsConsent()) return;

    let maxScroll = 0;
    const thresholds = [25, 50, 75, 90];
    const tracked = new Set<number>();

    const handleScroll = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );

      if (scrollPercent > maxScroll) {
        maxScroll = scrollPercent;
        
        thresholds.forEach(threshold => {
          if (scrollPercent >= threshold && !tracked.has(threshold)) {
            tracked.add(threshold);
            trackEvent({
              category: 'Scroll Depth',
              action: `${threshold}%`,
              label: location.pathname
            });
          }
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname, trackEvent]);

  // Track time on page
  const trackTimeOnPage = useCallback(() => {
    if (!hasAnalyticsConsent()) return;

    const startTime = Date.now();
    const intervals = [30, 60, 120, 300]; // 30s, 1m, 2m, 5m
    const tracked = new Set<number>();

    const checkTime = () => {
      const timeSpent = Math.floor((Date.now() - startTime) / 1000);
      
      intervals.forEach(interval => {
        if (timeSpent >= interval && !tracked.has(interval)) {
          tracked.add(interval);
          trackEvent({
            category: 'Time on Page',
            action: `${interval}s`,
            label: location.pathname
          });
        }
      });
    };

    const timer = setInterval(checkTime, 10000); // Check every 10 seconds
    return () => clearInterval(timer);
  }, [location.pathname, trackEvent]);

  return {
    trackEvent,
    trackForm,
    trackScrollDepth,
    trackTimeOnPage
  };
}